import React, { FC, Fragment } from 'react';
import { useFormikContext } from 'formik';
import { Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import PublishIcon from '@material-ui/icons/Publish';

import { ContentInput, S3ObjectInput, S3VideoObjectInput } from 'api';
import { UploadDialog, Uploader, UploaderContentTypes, UploadInput } from 'components';
import { useClient } from 'hooks';

import { AspectRatios } from 'types/app';
import { usePostContext } from '../../PostContext';

interface IProps {
  onImageUpload: () => void;
}

const useStyles = makeStyles(theme => ({
  uploadButton: {
    backgroundColor: 'rgba(56, 154, 255, .1)',
    border: 'none',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1.5, 2),
  },
}));

const MediaUpload: FC<IProps> = ({ onImageUpload }) => {
  const classes = useStyles();
  const client = useClient();
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext<ContentInput>();
  const { dispatch } = usePostContext();

  function handleSave(s3Object: S3ObjectInput | S3VideoObjectInput, type: UploaderContentTypes) {
    if (type === UploaderContentTypes.Video) {
      setFieldValue('content_object.media.video', { ...s3Object, uploading: true });
      setFieldValue('content_object.photos', null);

      if (!client.public_post_page) {
        setFieldValue('is_shareable', false);
        dispatch({ type: 'share', value: false });
      }
    }

    if (type === UploaderContentTypes.Image || type === UploaderContentTypes.GIF) {
      setFieldValue('content_object.photos', [s3Object]);
      setFieldValue('content_object.media', null);
      setFieldValue('is_shareable', true);

      // Backdoor to share form since our context is the post form
      onImageUpload();
    }
  }

  return (
    <Uploader onSave={handleSave} data-testid="uploader">
      {uploaderProps => (
        <Fragment>
          <UploadInput accept="video/*,image/*" onUpload={uploaderProps.onUpload}>
            <Button component="span" variant="contained" className={classes.uploadButton}>
              <PublishIcon color="primary" />
              <Typography color="primary" variant="body2">
                {t('posting:upload')}
              </Typography>
            </Button>
          </UploadInput>
          <UploadDialog
            aspectRatio={AspectRatios.none}
            i18nKeyTitle="posting:photo"
            i18nKeyReplace="components:replaceImage"
            {...uploaderProps}
          />
        </Fragment>
      )}
    </Uploader>
  );
};

export default MediaUpload;
